import React, { useEffect, useState } from "react";
import {
 FiArrowLeft,
  FiUser,
  FiUserPlus,
  FiSmartphone,
  FiMail,
  FiCalendar,
  FiDollarSign,
  FiFileText,
  FiBookOpen,
  FiGlobe,
  FiSettings,
  FiUsers,
  FiTrendingUp,
} from "react-icons/fi";
import "../assets/css/LeadDetails.css";
import { useNavigate } from "react-router-dom";
import { getAdminPanelCountsAPI, getCompanyData } from "../Service/apiService";
let cachedStats = {
  totalLeads: 0,
    uniqueLeads: 0,
    freeTrial: 0,
    trialFull: 0, // hardcoded 0
    requestDemo: 0,
    estimatedPricing: 0,
    hsnPrice: 0,
    apiPrice: 0,
    resourcesReport: 0,
    emailSubscribers: 0,
    leoshServices: 0,
    eventLeads: 0,
};
const hasValidStats = (stats) => {
  return Object.values(stats).some(value => value > 0);
};

export default function LeadsDetails() {
  const navigate = useNavigate();

  const [stats, setStats] = useState(() => ({ ...cachedStats }));
 
  const [loading, setLoading] = useState(() => !hasValidStats(cachedStats));

  useEffect(() => {
    // Only fetch if we don't have valid cached data
    if (!hasValidStats(cachedStats)) {
      fetchLeadsStats();
    }
  }, []);

  const fetchLeadsStats = async () => {
     setLoading(true);
    try {
      // 1. Fetch leads data
      const leadsResponse = await getAdminPanelCountsAPI();
      const leadsStats = leadsResponse?.result || {};

      // 2. Fetch companies to get leoshServices (leadDateCompanies)
      const companyResponse = await getCompanyData("all");
      const companies = companyResponse?.data || [];

      // Filter non-internal companies with leadsDate
      const isNotInternal = (c) =>
        (c.companyType || "").toLowerCase() !== "internal";
      const leadDateCompanies = companies.filter(
        (c) => c.leadsDate && isNotInternal(c)
      );
      const leoshCount = leadDateCompanies.length;

      // 3. Extract counts from arrays
      const computed = {
        totalLeads: leadsStats?.totalCount ?? 0,
        uniqueLeads: Array.isArray(leadsStats?.totalUniqueLeads)
          ? leadsStats.totalUniqueLeads.length
          : 0,
        freeTrial: Array.isArray(leadsStats?.freeTrialUserData)
          ? leadsStats.freeTrialUserData.length
          : 0,
        trialFull: 0,
        requestDemo: Array.isArray(leadsStats?.demoUserData)
          ? leadsStats.demoUserData.length
          : 0,
        estimatedPricing: Array.isArray(leadsStats?.pricingUserData)
          ? leadsStats.pricingUserData.length
          : 0,
        hsnPrice: Array.isArray(leadsStats?.hsnUserData)
          ? leadsStats.hsnUserData.length
          : 0,
        apiPrice: Array.isArray(leadsStats?.apiCalculatorUserData)
          ? leadsStats.apiCalculatorUserData.length
          : 0,
        resourcesReport: Array.isArray(leadsStats?.resourceUserData)
          ? leadsStats.resourceUserData.length
          : 0,
        emailSubscribers: Array.isArray(leadsStats?.subscribedUserData)
          ? leadsStats.subscribedUserData.length
          : 0,
        leoshServices: leoshCount,
        eventLeads: Array.isArray(leadsStats?.eventData)
          ? leadsStats.eventData.length
          : 0,
      };
    setStats(computed);
    cachedStats = { ...computed };

    } catch (err) {
      console.error("Error loading leads stats:", err);
    }
    finally {
      setLoading(false);
    }

  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "—";
    return Number(num).toLocaleString();
  };

  const metrics = [
    {
      id: "total-leads",
      key: "total",
      label: "Total Leads",
      value: formatNumber(stats.totalLeads),
      icon: FiTrendingUp,    
      color: "rgb(5, 55, 99)",
    },
    {
      id: "unique-leads",
      key: "totalUniqueLeads",
      label: "Total Unique Leads",
      value: formatNumber(stats.uniqueLeads),
      icon: FiUser,          
      color: "rgb(5, 55, 99)",
    },
    {
      id: "free-trial",
      key: "freeTrialUserData",
      label: "Free Access Trial",
      value: formatNumber(stats.freeTrial),
      icon: FiSmartphone,   
      color: "rgb(5, 55, 99)",
    },
    {
      id: "trial-full",
      key: "trialFull",
      label: "Trial Full Access",
      value: formatNumber(stats.trialFull),
      icon: FiSettings,   
      color: "rgb(5, 55, 99)",
    },
    {
      id: "request-demo",
      key: "demoUserData",
      label: "Request Demo",
      value: formatNumber(stats.requestDemo),
      icon: FiCalendar,      
      color: "rgb(5, 55, 99)",
    },
    {
      id: "estimated-pricing",
      key: "pricingUserData",
      label: "Estimated Pricing",
      value: formatNumber(stats.estimatedPricing),
      icon: FiDollarSign,   
      color: "rgb(5, 55, 99)",
    },
    {
      id: "hsn-price",
      key: "hsnUserData",
      label: "HSN Code Avg. Price",
      value: formatNumber(stats.hsnPrice),
      icon: FiGlobe,     
      color: "rgb(5, 55, 99)",
    },
    {
      id: "api-price",
      key: "apiCalculatorUserData",
      label: "API Calculator Avg. Price",
      value: formatNumber(stats.apiPrice),
      icon: FiSettings,   
      color: "rgb(5, 55, 99)",
    },
    {
      id: "resources-report",
      key: "resourceUserData",
      label: "Resources and Report",
      value: formatNumber(stats.resourcesReport),
      icon: FiBookOpen,  
      color: "rgb(5, 55, 99)",
    },
    {
      id: "email-subscribers",
      key: "subscribedUserData",
      label: "Email Subscribers",
      value: formatNumber(stats.emailSubscribers),
      icon: FiMail,
      color: "rgb(5, 55, 99)",
    },
    {
      id: "leosh-services",
      key: "leadDateCompanies",
      label: "Leosh Services",
      value: formatNumber(stats.leoshServices),
      icon: FiUsers,    
      color: "rgb(5, 55, 99)",
    },
    {
      id: "event-leads",
      key: "eventData",
      label: "Event Leads",
      value: formatNumber(stats.eventLeads),
      icon: FiCalendar,     
      color: "rgb(5, 55, 99)",
    },
  ];

  const goToTable = (key) => {
    // Map to old onClick keys if needed
    if (key === "leadDateCompanies") {
    navigate("/dashboard/companies/leadDateCompanies");
  } else {
    navigate(`/dashboard/leads/${key}`);
  };
}

   const NumberSkeleton = () => (
    <div className="number-skeleton">
      <div className="skeleton-loader"></div>
    </div>
  );

  return (
    <div className="leads-details-container">
     

      <div className="metrics-grid">
        {metrics.map(({ id, label, value, icon: Icon, color, key }) => (
          <div key={id} className="metric-card" onClick={() => goToTable(key)}>
            <div className="metric-icon" style={{ background: color }}>
              <Icon size={18} />
            </div>
            <p className="metric-label">{label}</p>
            {loading ? (
              <NumberSkeleton />
            ) : (
              <p className="metric-value">{value}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
